import { useState, useRef, useEffect, useCallback } from 'react';
import { B, radius, space } from '../constants/brand';
import { api } from '../api/client';
import { getAccessToken } from '../config/auth';
import { Btn, Spinner, ErrorMsg, Card, useToast, Icon, Modal, ModalFooter, clickable } from '../components/ui';
import { parseLocationsCSV } from '../utils/csvParsers';
import SageExcludedAccountsPanel from '../components/shared/SageExcludedAccountsPanel';
import SageMergeSettingsPanel from '../components/shared/SageMergeSettingsPanel';
import { describeOutcome } from '../utils/sageEmailOutcome';
import {
  PROCESS_TIMEOUT_MS, WATCH_MS, WATCH_POLL_MS,
  isImportSettled, isImportStarted, findImportRow, uploadOutcome,
} from '../utils/sageImportWatch';
import { fmt, formatInstant } from '../utils/format';

// Upload-card message colours. Keyed on the outcome's `tone` rather than a
// prefix match on its text, so rewording a message can never turn an error
// green — or, as before, colour a still-running import as a failure.
const UPLOAD_MSG_STYLE = {
  success: { bg: '#f0fdf4', fg: B.green,  border: '#bbf7d0' },
  // An import that landed its rows but left duplicates live: not a failure (the
  // rows are in and re-running would import them a third time), not a success
  // either. It needs its own colour or it reads as one of the two.
  warn:    { bg: '#fffbeb', fg: B.amber,  border: '#fde68a' },
  error:   { bg: '#fef2f2', fg: B.red,    border: '#fecaca' },
  info:    { bg: B.surfaceMuted, fg: B.textMd, border: B.border },
};

// ── Shared CSV drop zone sub-component ──────────────────────────────────────
const CsvDropZone = ({ label, description, onFile, file, children }) => {
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();
  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
        onClick={() => fileRef.current.click()}
        style={{
          border: `2px dashed ${dragging ? B.navy : B.border}`, borderRadius: 8,
          padding: '20px 16px', textAlign: 'center',
          background: dragging ? B.tealLt : B.surfaceMuted,
          cursor: 'pointer', marginBottom: 12, transition: 'all 0.15s',
        }}>
        <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }} />
        <div style={{ display: 'flex', justifyContent: 'center', color: B.tealDk, marginBottom: 4 }}><Icon name="sage" size={22} /></div>
        <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>
          {file ? file.name : label}
        </div>
        <div style={{ fontSize: 11, color: B.textMd, marginTop: 2 }}>{description}</div>
      </div>
      {children}
    </div>
  );
};

// ── Locations import sub-component ──────────────────────────────────────────
// What the API did AFTER writing the rows, in words. A Locations import
// rewrites the master the Capital Deployment widget files codes against and
// moves the ParentID the entity attachments are derived from, so the route
// re-runs both and reports each; a refresh that failed has to read as that
// rather than as an import that quietly changed nothing on the dashboard.
const locationsFollowUpLine = (fu) => {
  if (!fu) return null;
  const parts = [];
  const rec = fu.reconcile;
  if (rec?.ok) parts.push(`entity attachments reviewed (${rec.attached || 0} attached, ${rec.detached || 0} removed)`);
  else if (rec) parts.push(`entity review did not run (${rec.reason || rec.error || rec.skipped || 'unknown'})`);
  const snap = fu.snapshot;
  if (snap?.ok) parts.push(`Capital Deployment snapshot recomputed (${snap.locationCount ?? '?'} untagged code(s))`);
  else if (snap) parts.push(`Capital Deployment snapshot NOT refreshed (${snap.skipped || snap.error || 'unknown'})`);
  return parts.length ? parts.join(' · ') : null;
};

const LocationsImportPanel = () => {
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);

  const handleLocationsFile = async (f) => {
    setFile(f); setResult(null);
    const rows = await parseLocationsCSV(f);
    setPreview(rows);
  };

  const runImport = async () => {
    setImporting(true); setResult(null);
    try {
      // Long timeout: after the upsert the API re-derives the deal entity
      // attachments and recomputes the Capital Deployment snapshot (a whole-GL
      // aggregation). The 10 s default would report a timeout over an import
      // that landed — the same lesson as the ledger upload on this page.
      const res = await api.post('sage/locations/import', { locations: preview }, { timeout: 300000 });
      setResult({ ok: true, ...res });
      setFile(null); setPreview(null);
    } catch (e) { setResult({ ok: false, error: e.message }); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <CsvDropZone
        label="Drop Locations CSV here or click to browse"
        description="Columns: Location ID, Name, Parent, Manager (older Location ID / Parent ID / Parent - Name layout also works). The file is the whole roster: a location in Harvus that is not in the file is marked deleted."
        file={file}
        onFile={handleLocationsFile}>
        {preview && (
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 8 }}>
            {preview.length} locations parsed
            {preview.slice(0,3).map((r,i) => (
              <span key={i} style={{ marginLeft: 8, background: B.surfaceMuted, padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>{r.sageCode}</span>
            ))}
            {preview.length > 3 && <span style={{ marginLeft: 4, color: B.textLt }}>+{preview.length-3} more</span>}
          </div>
        )}
      </CsvDropZone>
      <div style={{ display: 'flex', gap: 8 }}>
        {preview && <Btn disabled={importing} onClick={runImport} size="sm">{importing ? 'Importing…' : `Import ${preview.length} Locations`}</Btn>}
        {file && <Btn variant="ghost" size="sm" onClick={() => { setFile(null); setPreview(null); setResult(null); }}><Icon name="close" size={13} /> Clear</Btn>}
      </div>
      {result && (
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: result.ok ? '#f0fdf4' : '#fef2f2',
          color: result.ok ? B.green : B.red,
          border: `1px solid ${result.ok ? '#bbf7d0' : '#fecaca'}`,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name={result.ok ? 'check' : 'warning'} size={13} /> {result.ok ? `${result.inserted} inserted, ${result.updated} updated` : result.error}</span>
          {result.ok && locationsFollowUpLine(result.followUp) && (
            <div style={{ marginTop: 4, color: B.textMd }}>{locationsFollowUpLine(result.followUp)}</div>
          )}
        </div>
      )}
      {/* THE EXPORT IS THE ROSTER: a location in the database but not in the file
          is marked deleted by the import. That is a change to rows nobody typed,
          so it is said here, verbatim from the API — and a retired location a
          live deal is still tagged to is the part worth reading, since that
          deal's Sage figures now read as $0. */}
      {result?.ok && result.note && (
        <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: result.retiredTagged?.length ? '#fff7ed' : B.surfaceMuted,
          color: result.retiredTagged?.length ? '#9a3412' : B.textMd,
          border: `1px solid ${result.retiredTagged?.length ? '#fed7aa' : B.border}`,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 5 }}><Icon name="warning" size={13} /> <span>{result.note}</span></span>
        </div>
      )}
    </div>
  );
};

// ── Email import diagnostics & catch-up panel ───────────────────────────────
// Sage GL/AR/… reports arrive by email (import+gl@harvus.com, etc.) and are
// normally imported automatically by a Graph webhook. When one doesn't show up,
// this panel says why (subscription / mailbox-access health) and lets an admin
// pull the mailbox in manually — the catch-up path the webhook alone can't cover.
// Each outcome status maps to a display label AND a severity. Severity drives the
// banner styling so a benign "nothing to import" result (no CSV, no attachments,
// already read) is not painted as an alarming red error — only genuine failures
// (an exception, a message we cannot read) are shown as errors.
const STATUS_LABEL = {
  imported:        { text: 'imported',               severity: 'success' },
  'read-skip':     { text: 'already read (skipped)', severity: 'info' },
  'duplicate-skip':{ text: 'already imported (skipped)', severity: 'info' },
  'no-import-type':{ text: 'no import type matched', severity: 'warn' },
  'no-attachments':{ text: 'no attachments',         severity: 'info' },
  'no-csv':        { text: 'no CSV attachment',       severity: 'warn' },
  'not-found':     { text: 'not found',               severity: 'error' },
  error:           { text: 'error',                   severity: 'error' },
};

// Per-message outcome tone → colour, for the "Recent messages in mailbox" list.
// success = it imported; pending = queued/processing; error = the import
// failed; warn = read before processing (skipped — the one case Reprocess
// fixes); info = benign (unread / no attachment / no type); unknown = the
// lookup failed, which must not read as either success or skip.
const OUTCOME_COLOR = {
  success: B.tealDk,
  pending: B.textMd,
  error:   B.red,
  warn:    B.amber,
  info:    B.textLt,
  unknown: B.textMd,
};

// Banner palette keyed by severity. success = green, info = neutral gray,
// warn = amber (benign but noteworthy), error = red (something actually failed).
const SEVERITY_STYLE = {
  success: { bg: '#f0fdf4', border: '#bbf7d0', color: '#16a34a' },
  info:    { bg: '#f8fafc', border: '#e2e8f0', color: '#475569' },
  warn:    { bg: '#fffbeb', border: '#fde68a', color: '#b45309' },
  error:   { bg: '#fef2f2', border: '#fecaca', color: '#dc2626' },
};


// ── Sage entity review ──────────────────────────────────────────────────────
// The reconcile that attaches a deal's holding company runs by itself — on
// every deal-detail save and at the end of a Locations import — and reports to
// nobody, which is right for something nobody asked for. This is the other
// half: somebody who has just fixed a location's parent in Sage, or untangled a
// conflict, wants to see it take effect NOW rather than by editing an unrelated
// deal to trigger it.
//
// It calls the SAME function the automatic runs call, so "run it now" and "let
// it run" cannot diverge — and unlike them it reports what it found, because
// somebody is standing there waiting for an answer.
const EntityReviewPanel = () => {
  const [open, setOpen]       = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [err, setErr]         = useState(null);

  const run = async () => {
    setRunning(true); setErr(null);
    try {
      // Whole-book review. The tables are small (deal details, locations,
      // attachments — never the GL), but the client default of 10s is meant for
      // an ordinary read, and a review that reports a timeout over a run that
      // actually succeeded is the SageImport lesson from one screen over.
      setResult(await api.post('deals/sage-entities/reconcile', {}, { timeout: 120000 }));
    } catch (e) {
      setErr(e.message || 'Review failed');
    } finally { setRunning(false); }
  };

  const box = (sev) => {
    const st = SEVERITY_STYLE[sev] || SEVERITY_STYLE.info;
    return {
      marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12,
      background: st.bg, color: st.color, border: `1px solid ${st.border}`,
    };
  };

  const d          = result?.details;
  const conflicts  = d?.conflicts || [];
  const attached   = d?.attached  || [];
  // A run that changed nothing is the NORMAL outcome of a healthy book, so it
  // has to read as success rather than as "nothing happened, did it work?".
  const changed    = (result?.attached || 0) + (result?.detached || 0);

  return (
    <Card style={{ marginBottom: 24 }}>
      <div {...clickable(() => setOpen(!open))} aria-expanded={open}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: B.tealDk, display: 'inline-flex' }}><Icon name="pin" size={18} /></span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.text }}>Deal entity links</div>
            <div style={{ fontSize: 12, color: B.textMd }}>
              Reviews every location’s parent in Sage and attaches each holding entity to the deal that owns it.
            </div>
          </div>
        </div>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>
            This runs automatically whenever a capital detail is saved and after a Locations
            import. Run it by hand after fixing a location’s parent in Sage, or after
            untangling an entity that was claimed by two deals.
          </div>
          <Btn size="sm" disabled={running} onClick={run}>
            {running ? 'Reviewing…' : 'Review locations & tag entities'}
          </Btn>

          {err && <div style={box('error')}>{err}</div>}

          {result && !result.ok && (
            <div style={box('warn')}>
              {result.reason || result.error || 'The review could not run.'}
            </div>
          )}

          {result && result.ok && (
            <>
              <div style={box(conflicts.length ? 'warn' : 'success')}>
                {changed === 0
                  ? 'Reviewed — every entity was already attached to the right deal.'
                  : `Reviewed — ${result.attached} attached, ${result.detached} removed.`}
                {conflicts.length > 0 && ` ${conflicts.length} entity(s) are claimed by more than one deal and were left off all of them.`}
                {result.notified > 0 && ' Neil and Kurt have been emailed.'}
              </div>

              {/* Named, not counted: "1 conflict" is not actionable. */}
              {conflicts.map(c => (
                <div key={c.sageCode} style={box('error')}>
                  <strong>{c.displayName || c.sageCode}</strong> ({c.sageCode}) is claimed by{' '}
                  {c.dealNames.join(' and ')}. Split the entity in Sage, or re-point the location
                  that is on the wrong deal.
                </div>
              ))}

              {attached.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: B.textMd, marginBottom: 6 }}>
                    Entities attached ({attached.length})
                  </div>
                  {attached.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: B.text, padding: '2px 0' }}>
                      {a.displayName || a.sageCode}
                      <span style={{ color: B.textLt, fontFamily: 'monospace', fontSize: 11 }}> {a.sageCode}</span>
                      <span style={{ color: B.textMd }}> → {a.dealName}</span>
                      {a.source === 'user' && <span style={{ color: B.textLt, fontSize: 11 }}> · added by hand</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
};

const EmailImportPanel = () => {
  const { toast } = useToast();
  const [open,         setOpen]         = useState(false);
  const [diag,         setDiag]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [scanning,     setScanning]     = useState(false);
  const [scanResult,   setScanResult]   = useState(null);
  const [reprocessing, setReprocessing] = useState(null);
  const [banner,       setBanner]       = useState(null);

  // silent → refresh diagnostics without clearing an outcome banner/scan result
  // the caller just set (otherwise the refresh wipes what the user needs to see).
  // Diagnostics makes several Graph round-trips, so give it more than the 10s
  // default; scan/reprocess import CSVs inline (thousands of rows each) and need
  // a much larger budget or the client aborts while the server is still working.
  const loadDiag = useCallback(async ({ silent = false } = {}) => {
    if (!silent) { setLoading(true); setBanner(null); setScanResult(null); }
    try { setDiag(await api.get('sage-email/diagnostics', { timeout: 30000 })); }
    catch (e) { if (!silent) setBanner({ sev: 'error', text: e.message }); }
    finally { if (!silent) setLoading(false); }
  }, []);

  const scan = async () => {
    setScanning(true); setBanner(null); setScanResult(null);
    try {
      const r = await api.post('sage-email/scan', {}, { timeout: 180000 });
      setScanResult(r);
      if (r.imported > 0) loadDiag({ silent: true });
    } catch (e) { setBanner({ sev: 'error', text: e.message }); }
    finally { setScanning(false); }
  };

  const reprocess = async (m) => {
    setReprocessing(m.id);
    setBanner(null); setScanResult(null);
    try {
      const r = await api.post('sage-email/scan', { messageId: m.id }, { timeout: 120000 });
      const o = (r.outcomes && r.outcomes[0]) || {};
      const meta = STATUS_LABEL[o.status] || {};
      const ok = o.status === 'imported';
      const sev = meta.severity || (ok ? 'success' : 'warn');
      const label = meta.text || o.status || 'done';
      // Prefer the error text; else the import type; and when nothing could be
      // imported, list what WAS attached so it's clear why (e.g. a PDF report).
      const found = (o.attachments && o.attachments.length)
        ? ` (found: ${o.attachments.join(', ')})` : '';
      const detail = o.error
        ? ` — ${o.error}`
        : (o.importType ? ` · ${o.importType}${found}` : found);
      await loadDiag({ silent: true });   // refresh first so the banner set below survives
      setBanner({ sev, text: `${m.subject || '(no subject)'} → ${label}${detail}` });
      if (ok) toast.success('Import queued from email');
    } catch (e) { setBanner({ sev: 'error', text: e.message }); }
    finally { setReprocessing(null); }
  };

  // Accepts a severity ('success' | 'info' | 'warn' | 'error'); a boolean is still
  // honored (true → success, false → error) for the plain success/failure callers.
  const box = (sev) => {
    const key = sev === true ? 'success' : sev === false ? 'error' : sev;
    const s = SEVERITY_STYLE[key] || SEVERITY_STYLE.error;
    return {
      marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    };
  };

  const sub = diag?.subscription;
  const mb  = diag?.mailboxAccess;

  return (
    <Card style={{ marginBottom: 24 }}>
      <div {...clickable(() => { const n = !open; setOpen(n); if (n && !diag) loadDiag(); })} aria-expanded={open}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: B.tealDk, display: 'inline-flex' }}><Icon name="emailInbox" size={18} /></span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.text }}>Email Import</div>
            <div style={{ fontSize: 12, color: B.textMd }}>Reports emailed to import+gl@harvus.com (etc.) import automatically. Check status or pull one in if it didn't arrive.</div>
          </div>
        </div>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Btn size="sm" variant="secondary" disabled={loading} onClick={loadDiag}>
              <Icon name="refresh" size={13} /> {loading ? 'Checking…' : 'Check status'}
            </Btn>
            <Btn size="sm" disabled={scanning} onClick={scan}>
              {scanning ? 'Scanning…' : 'Scan mailbox now'}
            </Btn>
          </div>

          {banner && <div style={box(banner.sev)}>{banner.text}</div>}

          {scanResult && (
            <div style={box(scanResult.imported > 0 ? 'success' : scanResult.errors > 0 ? 'error' : 'info')}>
              <div>
                Scanned {scanResult.scanned} message(s): {scanResult.imported} imported, {scanResult.skipped} skipped
                {scanResult.errors > 0 ? `, ${scanResult.errors} error(s)` : ''}.
                {scanResult.imported === 0 && (scanResult.errors > 0
                  ? ' See the error(s) below.'
                  : ' Already-read messages are skipped — use “Reprocess” on a specific message below to force one.')}
              </div>
              {(scanResult.outcomes || []).filter(o => o.status === 'error').slice(0, 5).map((o, i) => (
                <div key={i} style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word' }}>
                  • {o.subject}: {o.error}
                </div>
              ))}
            </div>
          )}

          {loading && !diag && <div style={{ marginTop: 10 }}><Spinner /></div>}

          {diag && (
            <div style={{ marginTop: 4 }}>
              {/* Health rows */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginBottom: 12 }}>
                <HealthRow label="Import mailbox"   value={diag.env?.importMailbox || '— not set —'} good={!!diag.env?.importMailbox} />
                <HealthRow label="Graph access"     value={mb?.ok === true ? 'can read mailbox' : mb?.ok === false ? (mb.error || 'cannot read') : 'unknown'} good={mb?.ok === true} />
                <HealthRow label="Webhook subscription" value={sub?.liveFound === true ? `live${sub.liveExpiry ? ` · expires ${formatInstant(sub.liveExpiry)}` : ''}` : sub?.liveFound === false ? 'not found' : 'unknown'} good={sub?.liveFound === true} />
                <HealthRow label="Email imports so far" value={`${diag.imports?.emailTotal ?? '—'} total · ${diag.imports?.emailPending ?? '—'} pending`} good={null} />
              </div>

              {/* Hints */}
              {(diag.hints || []).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {diag.hints.map((h, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, fontSize: 12, color: B.textMd, padding: '4px 0', borderTop: i > 0 ? `1px solid ${B.border}` : 'none' }}>
                      <span style={{ color: B.amber, flexShrink: 0, display: 'inline-flex', marginTop: 1 }}><Icon name="info" size={13} /></span>
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Recent messages */}
              {Array.isArray(mb?.sample) && mb.sample.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, marginBottom: 6 }}>RECENT MESSAGES IN MAILBOX</div>
                  <div style={{ border: `1px solid ${B.border}`, borderRadius: 6, overflow: 'hidden' }}>
                    {mb.sample.map((m, i) => (
                      <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 220px 74px', gap: 8, alignItems: 'center', padding: '7px 10px', fontSize: 12, borderTop: i > 0 ? `1px solid ${B.border}` : 'none', background: i % 2 ? B.surfaceMuted : B.white }}>
                        <span style={{ color: B.textLt, fontSize: 11 }}>{m.receivedDateTime ? fmt.date(m.receivedDateTime) : '—'}</span>
                        <span>
                          <div style={{ color: B.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.subject || '(no subject)'}</div>
                          <div style={{ color: B.textLt, fontSize: 10 }}>{m.to || '—'}{m.isRead ? ' · read' : ' · unread'}</div>
                        </span>
                        {(() => {
                          // Whether the message IMPORTED, not just what it was
                          // addressed to: a read message with no log row was
                          // skipped and never landed (see api/src/sageEmailOutcome.js).
                          const { text, tone } = describeOutcome(m);
                          return (
                            <span title={m.outcome?.reason || m.outcome?.errorSummary || undefined}
                              style={{ fontSize: 11, fontWeight: 600, color: OUTCOME_COLOR[tone] || B.textMd }}>
                              {text}
                            </span>
                          );
                        })()}
                        <span style={{ textAlign: 'right' }}>
                          <button
                            disabled={reprocessing === m.id}
                            onClick={() => reprocess(m)}
                            title="Import this email now (forces past the already-read check)"
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: reprocessing === m.id ? 'default' : 'pointer', border: `1px solid ${B.navy}`, background: B.white, color: B.navy, fontFamily: 'inherit' }}>
                            {reprocessing === m.id ? '…' : 'Reprocess'}
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const HealthRow = ({ label, value, good }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
    <span style={{
      width: 8, height: 8, borderRadius: '50%', flexShrink: 0, alignSelf: 'center',
      background: good === true ? B.green : good === false ? B.red : B.textLt,
    }} />
    <span style={{ color: B.textMd, minWidth: 92 }}>{label}:</span>
    <span style={{ color: B.text, fontWeight: 500, wordBreak: 'break-word' }}>{value}</span>
  </div>
);

// ── Main SageImport screen ───────────────────────────────────────────────────
export const SageImport = () => {
  const { toast } = useToast();
  const [imports,       setImports]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [loadErr,       setLoadErr]       = useState(null);
  const [showArchived,  setShowArchived]  = useState(false);
  const [dragging,      setDragging]      = useState(false);
  const [file,          setFile]          = useState(null);
  const [importType,    setImportType]    = useState('GeneralLedger');
  const [uploading,     setUploading]     = useState(false);
  const [uploadMsg,     setUploadMsg]     = useState(null);
  const [errorModal,    setErrorModal]    = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const fileRef = useRef();
  const pollRef = useRef(null);

  const TRANSACTION_TYPES = ['GeneralLedger','AccountsReceivable','VendorPayables','Payroll','BankReconciliation','BudgetVsActual'];
  // Entities and ChartOfAccounts are deliberately NOT local: they go through
  // the same upload → SageImportLog → process pipeline as a ledger import, so
  // they land in the import history, are read by the SAME server-side parser
  // as an emailed export, and work for the formats a browser-side CSV parse
  // cannot read — a real .xlsx workbook and the HTML document Sage names .xls
  // (which is exactly what the "GL Accounts" report downloads as).
  const isLocalType = importType === 'Locations';

  const STATUS_COLOR = {
    Pending:           B.amber,
    Processing:        B.tealDk,
    Completed:         B.green,
    Failed:            B.red,
    Reverted:          B.textLt,
    // Step 2 (docs/specs/sage-transaction-merge.md): the merge path computed
    // a real diff and deliberately did not apply it — waiting on an operator,
    // not a failure, so it gets its own color rather than borrowing red's.
    NeedsConfirmation: B.amber,
  };

  // Returns the rows as well as storing them, so the upload watcher can read
  // its own import out of the same request that refreshes the table.
  const loadImports = useCallback(async () => {
    try {
      const rows = await api.get(`sage/imports?includeArchived=${showArchived}`);
      setImports(rows); setLoadErr(null);
      return rows;
    } catch (e) { setLoadErr(e.message); return null; }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { setLoading(true); loadImports(); }, [loadImports]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    const hasActive = (imports || []).some(i => i.status === 'Pending' || i.status === 'Processing');
    if (!hasActive) return;
    pollRef.current = setInterval(loadImports, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [imports, loadImports]);

  // Watch the import ROW until it settles — the process request's response is
  // not what decides the outcome (see utils/sageImportWatch.js). `posted` is
  // the in-flight request: its failure only matters if the row shows the
  // import was never picked up, so it is recorded here and arbitrated at the
  // end rather than thrown.
  const watchImport = async (importId, posted) => {
    let requestError = null, postSettled = false;
    posted.then(() => { postSettled = true; },
                (e) => { postSettled = true; requestError = e; });

    const deadline = Date.now() + WATCH_MS;
    let row = null;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, WATCH_POLL_MS));
      row = findImportRow(await loadImports(), importId) || row;
      if (isImportSettled(row?.status)) return { row, requestError, stillRunning: false };
      // The request is over and nothing ever picked the import up — there is
      // no run to wait for, so whatever the request said is the real answer.
      if (postSettled && row && !isImportStarted(row)) return { row, requestError, stillRunning: false };
    }
    return { row, requestError, stillRunning: true };
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setUploadMsg(null);
    try {
      const { importId, sasUrl } = await api.post('sage/upload-url', { fileName: file.name, importType });
      const res = await fetch(sasUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'text/csv' }, body: file });
      if (!res.ok) throw new Error(`Blob upload failed: ${res.status}`);
      setUploadMsg({ tone: 'info', text: 'Processing… a large export takes a few minutes.' });

      // Fired, not awaited for the answer: the import runs inline in this
      // request and routinely outlives both the client budget and the Static
      // Web Apps edge (~45s → a bodyless 500), while continuing server-side.
      const posted = api.post(`sage/imports/${importId}/process`, {}, { timeout: PROCESS_TIMEOUT_MS });
      const outcome = uploadOutcome(await watchImport(importId, posted));
      // Keep the selection on a real failure so it can be retried; anything
      // else means the file is in and the import owns it now.
      if (outcome.tone !== 'error') setFile(null);
      setUploadMsg(outcome);
      loadImports();
    } catch (e) { setUploadMsg({ tone: 'error', text: `Upload failed: ${e.message}` }); }
    finally { setUploading(false); }
  };

  // toCamel lowercases only the first char: ImportID → importID, ImportId → importId.
  // Handle both DB column name casings.
  const impId = (imp) => imp.importId ?? imp.importID;

  const exportAnnotatedCsv = async (imp) => {
    try {
      const token = await getAccessToken();
      const res = await fetch(`/api/sage/imports/${impId(imp)}/annotated-csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = imp.fileName.replace(/\.csv$/i, '') + '_annotated.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const archiveImport = async (imp) => {
    setActionLoading(impId(imp));
    try { await api.post(`sage/imports/${impId(imp)}/${imp.isArchived ? 'unarchive' : 'archive'}`, {}); await loadImports(); }
    catch (e) { toast.error(e.message); }
    finally { setActionLoading(null); }
  };

  const revertImport = (imp) => {
    setConfirmAction({
      message: `Revert "${imp.fileName}"? This will delete all ${imp.rowsSucceeded} inserted transactions, `
             + `then recompute every deal's cached cost and IRR and the Capital Deployment snapshot — `
             + `which takes a minute or two.`,
      onConfirm: async () => {
        setActionLoading(impId(imp));
        try {
          // LONG TIMEOUT, for the reason the upload watch documents: a revert
          // now runs two whole-book recomputes server-side, so the client's 10s
          // default would report a timeout over a revert that landed — and the
          // rows would really be gone while the screen said it had failed.
          const r = await api.post(`sage/imports/${impId(imp)}/revert`, {}, { timeout: PROCESS_TIMEOUT_MS });
          await loadImports();
          // The recomputes are best-effort server-side, so a revert can succeed
          // with stale figures behind it. Say so rather than reporting a clean
          // success — every Sage number on the platform is wrong until they run.
          if (r?.recomputed?.error) {
            toast.error(`Reverted ${r.rowsReverted} row(s), but the figures did not refresh: ${r.recomputed.error}`);
          } else {
            toast.success(`Reverted ${r?.rowsReverted ?? 0} row(s); costs, IRRs and the capital-deployment snapshot refreshed.`);
          }
        }
        catch (e) { toast.error(e.message); }
        finally { setActionLoading(null); }
      },
    });
  };

  // Step 2 (docs/specs/sage-transaction-merge.md), guard 3 (dry run): a
  // NeedsConfirmation import already has its diff — a real removal count that
  // tripped the blast-radius threshold — sitting on the row as `diffSummary`.
  // This is the one operator action that bypasses that threshold, so the
  // dialog shows the diff verbatim and the request itself carries an explicit
  // `confirmBlastRadius: true` (the API refuses a bare POST for the same
  // reason this dialog exists: nothing gets past the guard by accident).
  const confirmMergeImport = (imp) => {
    setConfirmAction({
      message: `Apply "${imp.fileName}"? ${imp.diffSummary || 'The diff'} — this was held for review because the `
             + `removal was larger than the configured threshold. Applying it changes what is live in the general `
             + `ledger, then recomputes every deal's cached cost and IRR and the Capital Deployment snapshot.`,
      onConfirm: async () => {
        setActionLoading(impId(imp));
        try {
          const r = await api.post(`sage/imports/${impId(imp)}/confirm-merge`,
            { confirmBlastRadius: true }, { timeout: PROCESS_TIMEOUT_MS });
          await loadImports();
          if (r?.recomputed?.error) {
            toast.error(`Applied (${r.added} added, ${r.removed} removed), but the figures did not refresh: ${r.recomputed.error}`);
          } else {
            toast.success(`Applied: ${r?.added ?? 0} added, ${r?.removed ?? 0} removed, ${r?.unchanged ?? 0} unchanged; costs, IRRs and the capital-deployment snapshot refreshed.`);
          }
        }
        catch (e) { toast.error(e.message); }
        finally { setActionLoading(null); }
      },
    });
  };

  const archiveAll = () => {
    const archivable = (imports || []).filter(i => !i.isArchived && (i.status === 'Completed' || i.status === 'Failed'));
    if (!archivable.length) return;
    setConfirmAction({
      message: `Archive ${archivable.length} completed/failed imports?`,
      onConfirm: async () => {
        setActionLoading('bulk');
        try { await Promise.all(archivable.map(i => api.post(`sage/imports/${impId(i)}/archive`, {}))); await loadImports(); }
        catch (e) { toast.error(e.message); }
        finally { setActionLoading(null); }
      },
    });
  };

  const hasPending      = (imports || []).some(i => i.status === 'Pending' || i.status === 'Processing');
  const archivableCount = (imports || []).filter(i => !i.isArchived && (i.status === 'Completed' || i.status === 'Failed')).length;



  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: B.text, marginBottom: 20 }}>Sage Import</div>

      {/* Single unified import card */}
      <div>
          {/* Upload card */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: B.textMd, display: 'block', marginBottom: 6, letterSpacing: 0.5 }}>TRANSACTION IMPORT</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {TRANSACTION_TYPES.map(t => (
                  <button key={t} onClick={() => { setImportType(t); setFile(null); setUploadMsg(null); }} style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                    border: `1px solid ${importType === t ? B.navy : B.border}`,
                    background: importType === t ? B.navy : B.white,
                    color: importType === t ? '#fff' : B.textMd,
                  }}>{t}</button>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: B.textMd, display: 'block', marginBottom: 6, letterSpacing: 0.5 }}>REFERENCE DATA</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setImportType('Locations'); setFile(null); setUploadMsg(null); }} style={{
                    padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    border: `2px solid ${importType === 'Locations' ? B.tealDk : B.border}`,
                    background: importType === 'Locations' ? '#e8f5f5' : B.white,
                    color: importType === 'Locations' ? B.tealDk : B.textMd,
                  }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="pin" size={14} /> Locations</span></button>
                  <button onClick={() => { setImportType('ChartOfAccounts'); setFile(null); setUploadMsg(null); }} style={{
                    padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    border: `2px solid ${importType === 'ChartOfAccounts' ? B.tealDk : B.border}`,
                    background: importType === 'ChartOfAccounts' ? '#e8f5f5' : B.white,
                    color: importType === 'ChartOfAccounts' ? B.tealDk : B.textMd,
                  }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="ledger" size={14} /> Chart of Accounts</span></button>
                  <button onClick={() => { setImportType('Entities'); setFile(null); setUploadMsg(null); }} style={{
                    padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    border: `2px solid ${importType === 'Entities' ? B.tealDk : B.border}`,
                    background: importType === 'Entities' ? '#e8f5f5' : B.white,
                    color: importType === 'Entities' ? B.tealDk : B.textMd,
                  }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="list" size={14} /> Source Entities</span></button>
                </div>
              </div>
            </div>

            {!isLocalType && <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
              onClick={() => fileRef.current.click()}
              style={{ border: `2px dashed ${dragging ? B.navy : B.border}`, borderRadius: 10, padding: '48px 24px', textAlign: 'center', background: dragging ? B.tealLt : B.surfaceMuted, transition: 'all 0.2s', cursor: 'pointer' }}>
              <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx,.txt" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
              <div style={{ display: 'flex', justifyContent: 'center', color: B.tealDk, marginBottom: 12 }}><Icon name="swap" size={30} strokeWidth={1.75} /></div>
              <div style={{ fontWeight: 700, fontSize: 15, color: B.text, marginBottom: 6 }}>{file ? file.name : 'Drop Sage export here'}</div>
              <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
                {file ? `${(file.size/1024).toFixed(0)} KB · ready to upload` : 'or click to browse — .csv, .xlsx, or the .xls Sage downloads'}
              </div>
              {!file
                ? <Btn variant="secondary" onClick={e => { e.stopPropagation(); fileRef.current.click(); }}>Choose File</Btn>
                : <Btn variant="ghost" onClick={e => { e.stopPropagation(); setFile(null); }}>× Clear</Btn>
              }
            </div>}
            {file && !isLocalType && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Btn disabled={uploading} onClick={upload}>{uploading ? (uploadMsg ? 'Processing…' : 'Uploading…') : 'Upload & Process'}</Btn>
              </div>
            )}
            {uploadMsg && (() => {
              const st = UPLOAD_MSG_STYLE[uploadMsg.tone] || UPLOAD_MSG_STYLE.info;
              return (
                <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 6, fontSize: 13,
                  background: st.bg, color: st.fg, border: `1px solid ${st.border}`,
                }}>{uploadMsg.text}</div>
              );
            })()}
          </Card>

          {/* Email import diagnostics & manual catch-up */}
          <EmailImportPanel />
          <EntityReviewPanel />
          <SageExcludedAccountsPanel />
          <SageMergeSettingsPanel />

          {/* Local CSV import panels — shown inline when Locations or Chart of Accounts selected */}
          {importType === 'Locations' && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: B.text, marginBottom: 4 }}>Locations Import</div>
              <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>Upload the Sage locations export (Location ID, Name, Parent, Manager). Matches on Location ID — updates existing, inserts new. Or email it to import+loc@harvus.com to import automatically.</div>
              <LocationsImportPanel />
            </Card>
          )}
          {importType === 'ChartOfAccounts' && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: B.text, marginBottom: 4 }}>Chart of Accounts Import</div>
              <div style={{ fontSize: 13, color: B.textMd }}>Upload the Sage <strong>GL Accounts</strong> report (Account number, Title, Normal balance) above — the .xls Sage downloads, a .csv or an .xlsx all work. Matches on the account number: updates existing, inserts new, and restores an account that was previously deleted. <strong>The report is the whole chart:</strong> an account in Harvus that is not in the report is marked deleted, and the import history row says which — including any still tagged to a live capital item, whose Sage figures then read as $0 until re-tagged. Or email the report to <strong>import+coa@harvus.com</strong> (or import+accounts@) to import it automatically, daily if Sage schedules it.</div>
            </Card>
          )}
          {importType === 'Entities' && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: B.text, marginBottom: 4 }}>Source Entities Import</div>
              <div style={{ fontSize: 13, color: B.textMd }}>Upload the Sage entity list (ID, Name, Federal ID) above. Matches on the entity ID — updates existing, inserts new; the two period-locking date columns are ignored. Used for the Source Entity on Real-Estate journal entries. Or email the export to import+ent@harvus.com to import it automatically.</div>
            </Card>
          )}

          {/* History header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: B.text }}>Import History</div>
              {hasPending && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: B.amber, fontWeight: 600 }}><Icon name="refresh" size={13} /> Processing… auto-refreshing</span>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => setShowArchived(v => !v)} style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${showArchived ? B.navy : B.border}`,
                background: showArchived ? B.navy : B.white,
                color: showArchived ? '#fff' : B.textMd,
              }}>{showArchived ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="check" size={13} /> Showing Archived</span> : 'Show Archived'}</button>
              {archivableCount > 0 && (
                <Btn size="sm" variant="ghost" disabled={actionLoading === 'bulk'} onClick={archiveAll}>
                  {actionLoading === 'bulk' ? '…' : `Archive All (${archivableCount})`}
                </Btn>
              )}
              <Btn size="sm" variant="ghost" onClick={loadImports}><Icon name="refresh" size={14} /> Refresh</Btn>
            </div>
          </div>

          {/* History list */}
          {loading ? <Spinner /> : loadErr ? <ErrorMsg msg={loadErr} onRetry={loadImports} /> : (
            <Card style={{ padding: 0 }}>
              {(!imports || imports.length === 0)
                ? <div style={{ padding: 32, textAlign: 'center', color: B.textLt, fontSize: 13 }}>
                    {showArchived ? 'No imports found.' : 'No active imports.'}
                  </div>
                : imports.map(imp => {
                  const isActing     = actionLoading === impId(imp);
                  const canArchive   = !imp.isArchived && ['Completed','Failed','Reverted'].includes(imp.status);
                  const canUnarchive = imp.isArchived;
                  const canRevert    = imp.status === 'Completed' && !imp.isArchived;
                  return (
                    <div key={impId(imp)} style={{
                      display: 'grid', gridTemplateColumns: '150px 1fr 72px 72px 72px 110px 100px',
                      padding: '12px 16px', borderBottom: `1px solid ${B.border}`, alignItems: 'center',
                      opacity: imp.isArchived ? 0.6 : 1, background: imp.isArchived ? B.surfaceMuted : '#fff',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = imp.isArchived ? B.surfaceSunken : B.surfaceMuted}
                      onMouseLeave={e => e.currentTarget.style.background = imp.isArchived ? B.surfaceMuted : '#fff'}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: B.text }}>
                          {imp.receivedUtc ? fmt.date(imp.receivedUtc) : imp.createdUtc ? fmt.date(imp.createdUtc) : '—'}
                        </div>
                        <div style={{ fontSize: 11, color: B.textLt }}>{imp.importType}</div>
                        {imp.isArchived && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: B.textLt, marginTop: 2 }}><Icon name="archive" size={11} /> archived</div>}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: B.text, fontWeight: 500 }}>{imp.fileName}</div>
                        {imp.importDateRangeStart && (
                          <div style={{ fontSize: 11, color: B.textLt }}>
                            Range: {fmt.date(imp.importDateRangeStart)} – {fmt.date(imp.importDateRangeEnd)}
                          </div>
                        )}
                        {imp.diffSummary && (
                          <div style={{ fontSize: 11, color: B.textMd, marginTop: 2 }}
                            title="What this import changed against what was already live for the same type and date range. Reported only — nothing is merged yet.">
                            Changes: {imp.diffSummary}
                          </div>
                        )}
                        {imp.errorSummary && (
                          <div style={{ fontSize: 11, color: B.red, marginTop: 2, cursor: 'pointer' }}
                            onClick={() => setErrorModal(imp.errorSummary)}
                            title="Click to see full error">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="warning" size={12} /> {imp.errorSummary.length > 80 ? imp.errorSummary.substring(0,80)+'… (click)' : imp.errorSummary}</span>
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{imp.rowsSucceeded ?? '—'}</div>
                        <div style={{ fontSize: 10, color: B.textLt }}>inserted</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: imp.rowsSkipped > 0 ? B.textMd : B.textLt }}>{imp.rowsSkipped ?? '—'}</div>
                        <div style={{ fontSize: 10, color: B.textLt }}>skipped</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: imp.rowsFailed > 0 ? B.red : B.textLt }}>{imp.rowsFailed ?? '—'}</div>
                        <div style={{ fontSize: 10, color: B.textLt }}>failed</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: `${STATUS_COLOR[imp.status] || B.textLt}20`, color: STATUS_COLOR[imp.status] || B.textLt }}>
                          {imp.status}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {isActing ? <span style={{ fontSize: 11, color: B.textLt }}>…</span> : <>
                          {imp.status === 'Completed' && <button onClick={() => exportAnnotatedCsv(imp)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer', border: `1px solid ${B.tealDk}`, background: B.white, color: B.tealDk, fontFamily: 'inherit' }}>↓ Export</button>}
                          {canArchive && <button onClick={() => archiveImport(imp)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer', border: `1px solid ${B.border}`, background: B.white, color: B.textMd, fontFamily: 'inherit' }}>Archive</button>}
                          {canUnarchive && <button onClick={() => archiveImport(imp)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer', border: `1px solid ${B.navy}`, background: B.white, color: B.navy, fontFamily: 'inherit' }}>Restore</button>}
                          {canRevert && <button onClick={() => revertImport(imp)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer', border: `1px solid ${B.red}`, background: B.white, color: B.red, fontFamily: 'inherit' }}>Revert</button>}
                          {imp.status === 'NeedsConfirmation' && <button onClick={() => confirmMergeImport(imp)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer', border: `1px solid ${B.amber}`, background: B.white, color: B.amber, fontFamily: 'inherit', fontWeight: 700 }}>Review &amp; Apply</button>}
                        </>}
                      </div>
                    </div>
                  );
                })
              }
            </Card>
          )}

          {/* Confirmation modal */}
          {confirmAction && (
            <Modal title="Confirm" width={480} onClose={() => setConfirmAction(null)}>
              <div style={{ fontSize: 14, color: B.text, marginBottom: space.xxl }}>{confirmAction.message}</div>
              <ModalFooter>
                <Btn variant="ghost" onClick={() => setConfirmAction(null)}>Cancel</Btn>
                <Btn variant="danger" onClick={() => { const fn = confirmAction.onConfirm; setConfirmAction(null); fn(); }}>Confirm</Btn>
              </ModalFooter>
            </Modal>
          )}
          {/* Error modal */}
          {errorModal && (
            <Modal title="Import Error Detail" width={560} onClose={() => setErrorModal(null)}>
              <div style={{ fontSize: 12, color: B.red, background: B.red + '10', padding: 12, borderRadius: radius.lg, border: `1px solid ${B.red}33`, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflowY: 'auto' }}>
                {errorModal}
              </div>
              <ModalFooter>
                <Btn variant="secondary" onClick={() => setErrorModal(null)}>Close</Btn>
              </ModalFooter>
            </Modal>
          )}
        </div>
      )}

    </div>
  );
};
